import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import { Sparkles, Send, ChevronRight, Wrench, Minimize2, Trash2, Copy, Check } from 'lucide-react'
import {
  clearChatHistory,
  loadChatHistory,
  resolveCompositeIds,
  sendChatStream,
  type ChatContextMeter,
  type ChatPersistedMessage,
  type ChatScopeKind,
  type ChatToolCall,
  type CompositeResolution,
} from '../api'

// Composite-id regexes. Matter is M-NNN+, artifact is
// CLIENT-VENDOR-TYPE-NNNNNN+ (uppercase letters, digits at the end).
// Both are word-boundary-anchored so they don't trigger inside random
// hex/etc. The artifact pattern requires 6+ digits to avoid grabbing
// hyphenated dates like 2025-06-30.
const MATTER_CODE_RE = /\bM-\d{3,}\b/g
const ARTIFACT_CODE_RE = /\b[A-Z]{2,5}-[A-Z]{2,5}-[A-Z]{2,5}-\d{6,}\b/g
function extractCompositeCodes(text: string): string[] {
  if (!text) return []
  const set = new Set<string>()
  for (const m of text.matchAll(MATTER_CODE_RE)) set.add(m[0])
  for (const m of text.matchAll(ARTIFACT_CODE_RE)) set.add(m[0])
  return Array.from(set)
}
// Replace every bare composite code with markdown link syntax pointing
// at our `composite:` URL scheme. Sequencing matters because regex
// .replaceAll is naive for shorter codes embedded in longer ones — we
// run the artifact (longer) pattern first.
function linkifyCompositeCodes(text: string): string {
  if (!text) return text
  return text
    .replace(ARTIFACT_CODE_RE, (m) => `[${m}](composite:${m})`)
    .replace(MATTER_CODE_RE, (m) => `[${m}](composite:${m})`)
}
// Walk a ReactNode tree to extract its text content. Used by the
// composite-link handler so it can compare the visible link text to
// the URL's composite code and prefer the text when they disagree.
function childrenToString(node: unknown): string {
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(childrenToString).join('')
  if (node && typeof node === 'object' && 'props' in node) {
    return childrenToString((node as { props?: { children?: unknown } }).props?.children)
  }
  return ''
}

type Props = {
  /** Display label for the scope chip in the header (e.g. matter name). */
  scopeLabel?: string | null
  scopeKind: ChatScopeKind
  scopeId: number | null
  /** Artifact the user has selected RIGHT NOW (independent of chat scope).
   *  Lets the AI know what "this" / "this email" refers to. */
  currentArtifactId?: number | null
  /** Optional: called when the user requests minimize. If omitted, no
   * minimize button is shown (caller is responsible for placement). */
  onMinimize?: () => void
}

/**
 * Chat panel — used both as the inline expanded panel (workspace 4th
 * column) and inside the floating bubble overlay. History is loaded
 * from the server keyed by (user, scope_kind, scope_id) and persists
 * across browsers + devices. localStorage is no longer used.
 */
export default function ChatPanel({
  scopeLabel, scopeKind, scopeId, currentArtifactId, onMinimize,
}: Props) {
  const qc = useQueryClient()
  const queryKey = ['chat-history', scopeKind, scopeId] as const

  const history = useQuery({
    queryKey,
    queryFn: () => loadChatHistory({ scope_kind: scopeKind, scope_id: scopeId }),
    staleTime: 0,
  })

  // Streaming state. `liveText` accumulates content deltas as they
  // arrive from the server; `toolStatus` is a short label shown while
  // a tool is running between content chunks. `errorMsg` is set on
  // stream error. `isStreaming` flips on/off across the whole turn.
  const [liveText, setLiveText] = useState('')
  const [toolStatus, setToolStatus] = useState<string | null>(null)
  const [streamErr, setStreamErr] = useState<string | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  // Optimistic user turn shown ABOVE the live assistant bubble while
  // streaming. Cleared on stream completion (history refetch supplies
  // the authoritative version).
  const [optimisticUser, setOptimisticUser] = useState<ChatPersistedMessage | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const startStream = useCallback(async (text: string) => {
    if (isStreaming) return
    setStreamErr(null)
    setLiveText('')
    setToolStatus(null)
    setIsStreaming(true)
    setOptimisticUser({
      id: -Date.now(), role: 'user', content: text,
      tool_calls: null, tool_call_id: null, tool_name: null,
      iterations: null, input_tokens: null, output_tokens: null, latency_ms: null,
      created_at: new Date().toISOString(),
    })
    const ctl = new AbortController()
    abortRef.current = ctl
    let streamCompletedSuccessfully = false
    try {
      for await (const ev of sendChatStream({
        message: text, scope_kind: scopeKind, scope_id: scopeId,
        current_artifact_id: currentArtifactId ?? null,
        signal: ctl.signal,
      })) {
        if (ev.type === 'chunk') {
          setLiveText((t) => t + ev.text)
          setToolStatus(null)
        } else if (ev.type === 'tool_call') {
          setToolStatus(`calling ${ev.name}…`)
        } else if (ev.type === 'tool_result') {
          setToolStatus(`${ev.name} ✓`)
        } else if (ev.type === 'error') {
          setStreamErr(ev.detail)
        } else if (ev.type === 'done') {
          streamCompletedSuccessfully = true
          break
        }
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'stream failed'
      setStreamErr(msg)
    }
    abortRef.current = null
    if (streamCompletedSuccessfully) {
      // Pull the authoritative server-side history BEFORE we clear the
      // local streaming state. Otherwise there's a blank-frame seam
      // between the streaming bubble vanishing and the real one
      // landing — looks like a flash / shrink. We await the refetch
      // first so the new turn is in `history.data.messages` by the
      // time the streaming bubble unmounts.
      await qc.invalidateQueries({ queryKey })
      // One more tick so React has flushed the new messages to the DOM.
      await new Promise((r) => setTimeout(r, 0))
    } else {
      // Stream errored — still refetch so the user-message persistence
      // (or rollback) is visible, but don't bother awaiting.
      qc.invalidateQueries({ queryKey })
    }
    setIsStreaming(false)
    setOptimisticUser(null)
    setLiveText('')
    setToolStatus(null)
  }, [isStreaming, scopeKind, scopeId, currentArtifactId, qc, queryKey])

  const clear = useMutation({
    mutationFn: () => clearChatHistory({ scope_kind: scopeKind, scope_id: scopeId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey })
    },
  })

  const [draft, setDraft] = useState('')
  const feedRef = useRef<HTMLDivElement>(null)
  const draftRef = useRef<HTMLTextAreaElement>(null)
  const messages = history.data?.messages ?? []
  const sending = isStreaming

  // Collect every composite code mentioned across all assistant messages
  // (including the body of any composite:… markdown link the AI wrote
  // explicitly), batch-resolve them once, and pass the map down to the
  // turn renderer. Resolution respects per-user privacy on the backend
  // — codes the user can't see drop out of the map and render as
  // plain text (no broken-link UI).
  const compositeCodes = useMemo(() => {
    const all = new Set<string>()
    for (const m of messages) {
      if (m.role !== 'assistant') continue
      for (const c of extractCompositeCodes(m.content || '')) all.add(c)
    }
    return Array.from(all).sort()
  }, [messages])
  const compositeQuery = useQuery({
    queryKey: ['chat-composite-resolutions', compositeCodes.join(',')],
    queryFn: () => resolveCompositeIds(compositeCodes),
    enabled: compositeCodes.length > 0,
    staleTime: 5 * 60_000,  // codes don't move under us within a session
  })
  const compositeMap: Record<string, CompositeResolution> =
    compositeQuery.data?.resolutions ?? {}

  // Auto-grow the textarea: starts ~1 row, grows as the user types,
  // capped at MAX_TEXTAREA_PX so the chat feed doesn't get pushed off
  // the screen on long drafts. Standard chat-input pattern (Slack,
  // ChatGPT, etc).
  const MIN_TEXTAREA_PX = 36   // ~ 1 line
  const MAX_TEXTAREA_PX = 140  // ~ 6 lines, then it scrolls
  useEffect(() => {
    const el = draftRef.current
    if (!el) return
    el.style.height = 'auto'
    const next = Math.min(MAX_TEXTAREA_PX, Math.max(MIN_TEXTAREA_PX, el.scrollHeight))
    el.style.height = `${next}px`
  }, [draft])

  // Auto-scroll on new messages and as content streams in.
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight
    }
  }, [messages.length, sending, liveText, toolStatus])

  function submit() {
    const text = draft.trim()
    if (!text || sending) return
    setDraft('')
    void startStream(text)
  }

  // Per Ken 2026-05-25: drop the "scoped" word — when in-matter, the
  // dot + matter name is enough; when global, no scope suffix at all
  // (and the title drops "Matter" too).
  const inMatter = scopeKind === 'matter' && scopeId != null
  const headerTitle = inMatter ? 'Consult Matter' : 'Consult'
  // Per Ken 2026-05-25 (refined): drop the leading "· " — the gap
  // between the title and the matter name is enough; the dot was
  // eating horizontal space without adding meaning.
  const scopeText = scopeLabel
    ? scopeLabel
    : inMatter
      ? `matter #${scopeId}`
      : scopeKind === 'artifact' && scopeId != null
        ? `artifact #${scopeId}`
        : ''

  const errorMsg = streamErr

  return (
    <aside className="me-scroll flex h-full flex-col bg-[color:var(--me-surface)]">
      {/* Header */}
      {/* leading-none on text + items-center on the row keeps the title,
          icon, and the smaller scope text on the same visual centerline.
          Without leading-none the 12px title's default line-height (1.5)
          adds asymmetric space and the text reads "low" next to the
          icon + buttons. */}
      <div className="flex items-center gap-2 border-b border-[color:var(--me-border)] px-3 py-2">
        <Sparkles size={13} className="me-text-mute shrink-0" />
        <span className="text-[12px] font-medium leading-none me-text shrink-0">{headerTitle}</span>
        {scopeText && (
          <span
            className="me-mono text-[10px] leading-none me-text-mute ml-auto min-w-0 truncate"
            title={scopeText}
          >
            {scopeText}
          </span>
        )}
        {history.data?.context && (
          <ContextMeter ctx={history.data.context} />
        )}
        {messages.length > 0 && (
          <button
            onClick={() => {
              const ok = window.confirm(
                'Delete this entire conversation? This wipes the server-side ' +
                'history and the saved context summary. Cannot be undone.'
              )
              if (ok) clear.mutate()
            }}
            disabled={clear.isPending}
            className="me-btn me-btn-outline shrink-0 px-1.5 py-0 text-[10px]"
            title="Delete this conversation (irreversible — wipes history + summary on the server)"
          >
            <Trash2 size={10} />
          </button>
        )}
        {onMinimize && (
          <button
            onClick={onMinimize}
            className="me-btn me-btn-outline shrink-0 px-1.5 py-0 text-[10px]"
            title="Minimize"
          >
            <Minimize2 size={10} />
          </button>
        )}
      </div>
      {history.data?.context?.has_summary && (
        <details className="border-b border-[color:var(--me-border)] bg-[color:var(--me-bg)] px-3 py-1 text-[10px] me-text-mute">
          <summary className="cursor-pointer hover:underline">
            ↑ earlier turns summarized to fit context
            {history.data.context.summarized_this_turn ? ' (just now)' : ''}
          </summary>
          {history.data.context.summary && (
            <div
              className="mt-1 whitespace-pre-wrap break-words text-[11px] me-text-dim"
              style={{ overflowWrap: 'anywhere' }}
            >
              {history.data.context.summary}
            </div>
          )}
        </details>
      )}
      {history.data?.context?.trimmed && !history.data?.context?.has_summary && (
        <div className="border-b border-[color:var(--me-border)] bg-[color:var(--me-bg)] px-3 py-1 text-[10px] me-text-mute">
          {history.data.context.dropped_count} earlier message
          {history.data.context.dropped_count === 1 ? '' : 's'} trimmed to fit context
        </div>
      )}

      {/* Message feed */}
      <div ref={feedRef} className="flex-1 overflow-auto px-3 py-3 text-[12px]">
        {history.isLoading && (
          <div className="me-pulse me-text-mute text-[11px]">loading history…</div>
        )}
        {!history.isLoading && messages.length === 0 && (
          <div className="rounded-sm border border-dashed border-[color:var(--me-border)] bg-[color:var(--me-bg)] p-2 text-[11px] me-text-mute">
            Ask about matters, artifacts, deadlines, or who's been involved.
          </div>
        )}
        {messages.map((m, i) => (
          <TurnRow key={m.id} message={m} messages={messages} index={i} compositeMap={compositeMap} />
        ))}
        {/* Optimistic user bubble while streaming — disappears on
            stream-end when history refetch supplies the real one. */}
        {optimisticUser && <TurnRow message={optimisticUser} messages={messages} index={messages.length} compositeMap={compositeMap} />}
        {/* Streaming assistant bubble.
            - Before first chunk: "thinking…" or "calling X…" status.
            - During stream: live markdown, but we split at the LAST
              newline. Everything up to the last \n is stable markdown
              that only re-parses when a NEW line completes; the
              trailing partial line renders as plain text. So mid-line
              growth never re-parses (no flicker from `**bold` →
              `**bold**` swapping nodes); the markdown tree only
              updates at safe paragraph/list boundaries. */}
        {sending && (
          <div className="mb-3">
            <div className="me-mono text-[10px] me-text-mute">assistant</div>
            {liveText ? (
              <div className="me-text me-stream-fade break-words me-prose">
                <StreamingMarkdown text={liveText} />
              </div>
            ) : toolStatus ? (
              <div className="me-text-mute text-[11px] flex items-center gap-1">
                <Wrench size={11} className="me-pulse" /> <span className="me-pulse">{toolStatus}</span>
              </div>
            ) : (
              <div className="me-text-mute text-[11px] me-pulse">thinking…</div>
            )}
          </div>
        )}
        {errorMsg && (
          <div className="mt-2 rounded-sm border border-[color:var(--me-warn)] bg-[color:var(--me-bg)] p-2 text-[11px] text-[color:var(--me-warn)]">
            {errorMsg}
          </div>
        )}
      </div>

      {/* Input — auto-grow textarea (1 → 6 lines) + square send button on
          the bottom row. Standard chat-input pattern. */}
      <div className="flex items-end gap-2 border-t border-[color:var(--me-border)] p-2">
        <textarea
          ref={draftRef}
          placeholder="Ask…  (Shift+Enter for newline)"
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              submit()
            }
          }}
          disabled={sending}
          className="me-input flex-1 resize-none overflow-y-auto text-[12px] leading-snug"
          style={{ minHeight: `${MIN_TEXTAREA_PX}px`, maxHeight: `${MAX_TEXTAREA_PX}px` }}
        />
        <button
          onClick={submit}
          disabled={!draft.trim() || sending}
          className="flex shrink-0 items-center justify-center rounded-sm bg-[color:var(--me-text)] text-[color:var(--me-surface)] disabled:opacity-30"
          style={{ width: MIN_TEXTAREA_PX, height: MIN_TEXTAREA_PX }}
          title="Send"
          aria-label="Send"
        >
          {sending ? <ChevronRight size={14} className="me-pulse" /> : <Send size={14} />}
        </button>
      </div>
    </aside>
  )
}


function TurnRow({ message, messages, index, compositeMap }: {
  message: ChatPersistedMessage
  messages: ChatPersistedMessage[]
  index: number
  compositeMap: Record<string, CompositeResolution>
}) {
  if (message.role === 'tool') return null  // tool turns are folded into the prior assistant
  if (message.role === 'user') {
    return (
      <div className="mb-3">
        <div className="me-mono text-[10px] me-text-mute">you</div>
        <div className="font-medium me-text whitespace-pre-wrap break-words">{message.content}</div>
      </div>
    )
  }
  // assistant. Two cases:
  // - Mid-loop (no content, has OpenAI-format tool_calls): we don't
  //   render those as standalone rows — they're an internal step.
  //   Folded into the next final assistant via the chip reconstruction
  //   below. Skip rendering anything here.
  // - Final (content non-empty): show the reply, AND reconstruct the
  //   chips from tool messages between this row and the prior user
  //   turn. The previous shortcut of stuffing a "log" into
  //   tool_calls of the final row caused the LLM to break on the
  //   next turn (see chat/views.py _persist_loop_result), so we now
  //   reconstruct in the frontend instead.
  const isFinalAssistant = !!(message.content || '').trim()
  if (!isFinalAssistant) {
    // Skip rendering mid-loop assistant rows entirely — their tools
    // get rolled up into the next final-assistant row.
    return null
  }
  const tools = reconstructToolChips(messages, index)
  // Pre-process the assistant content: any bare composite_id like
  // 'MOVU-GOOG-EMAL-032819' or 'M-019' becomes a markdown link with our
  // `composite:` URL scheme. The model's own `[label](composite:CODE)`
  // links pass through unchanged. The custom <a> handler below renders
  // these as REAL anchors with internal URLs so copy-link / middle-click
  // / cmd-click all work natively, but plain-click is intercepted to do
  // SPA navigation (no page reload).
  const renderableContent = useMemo(
    () => linkifyCompositeCodes(message.content || ''),
    [message.content],
  )
  const compositeUrl = (r: CompositeResolution): string =>
    r.kind === 'artifact' ? `/artifact/${r.id}` : `/workspace?matter=${r.id}`
  return (
    <div className="mb-3 group/turn">
      <div className="flex items-center gap-2">
        <div className="me-mono text-[10px] me-text-mute">assistant</div>
        {message.content && (
          <CopyButton text={message.content} />
        )}
      </div>
      <div className="me-text break-words me-prose">
        <ReactMarkdown
          // Pass-through URL transform: react-markdown's default
          // sanitizer strips any URL scheme that isn't in its allow-list
          // (http/https/mailto/tel). Our internal `composite:CODE`
          // scheme was being silently stripped to href="", so the
          // custom `a` component handler below never saw the
          // composite: prefix and fell through to the external-link
          // branch — hence the "renders as <a href='' target='_blank'>"
          // bug a user reported. Returning the URL unchanged keeps
          // composite: alive; everything else still renders as before.
          urlTransform={(uri: string) => uri}
          components={{
            p: (props) => <p className="mb-1.5 last:mb-0" {...props} />,
            ul: (props) => <ul className="my-1.5 list-disc pl-4 space-y-0.5" {...props} />,
            ol: (props) => <ol className="my-1.5 list-decimal pl-4 space-y-0.5" {...props} />,
            li: (props) => <li className="leading-snug" {...props} />,
            strong: (props) => <strong className="font-semibold" {...props} />,
            em: (props) => <em className="italic" {...props} />,
            code: (props) => (
              <code
                className="me-mono rounded bg-[color:var(--me-bg)] px-1 py-0.5 text-[11px]"
                {...props}
              />
            ),
            pre: (props) => (
              <pre
                className="me-mono my-2 max-h-[260px] overflow-auto rounded bg-[color:var(--me-bg)] p-2 text-[11px]"
                {...props}
              />
            ),
            h1: (props) => <div className="mb-1 mt-2 text-[13px] font-semibold" {...props} />,
            h2: (props) => <div className="mb-1 mt-2 text-[13px] font-semibold" {...props} />,
            h3: (props) => <div className="mb-1 mt-2 text-[12px] font-semibold" {...props} />,
            a: ({ href, children, ...rest }) => {
              if (typeof href === 'string' && href.startsWith('composite:')) {
                let code = href.slice('composite:'.length)
                // Defense against the model writing the wrong code in
                // the URL part of [label](composite:CODE) — when the
                // visible link text itself contains a composite code,
                // trust the visible text over the URL.
                const childText = childrenToString(children)
                const fromText = extractCompositeCodes(childText)[0]
                if (fromText && fromText !== code) code = fromText
                const r = compositeMap[code]
                if (!r) return <span>{children}</span>
                const internalUrl = compositeUrl(r)
                const title = r.name ? `${r.kind}: ${r.name}` : r.kind
                // Use react-router's <Link> for SPA navigation. It
                // renders a real <a href="..."> (so 'copy link address',
                // middle-click, cmd-click all work natively) AND
                // intercepts plain left-click to push history without
                // a page reload.
                return (
                  <Link
                    to={internalUrl}
                    className="underline decoration-dotted underline-offset-2 hover:decoration-solid"
                    title={title}
                  >
                    {children}
                  </Link>
                )
              }
              return <a className="underline" target="_blank" rel="noreferrer" href={href} {...rest}>{children}</a>
            },
          }}
        >
          {renderableContent}
        </ReactMarkdown>
      </div>
      {tools.length > 0 && (
        <details className="mt-1.5">
          <summary className="cursor-pointer me-mono text-[10px] me-text-mute hover:underline">
            <Wrench size={9} className="mr-1 inline-block" />
            {tools.length} tool {tools.length === 1 ? 'call' : 'calls'}
          </summary>
          <div className="mt-1 space-y-1">
            {tools.map((tc: ChatToolCall, idx: number) => (
              <div
                key={idx}
                className="rounded-sm border border-[color:var(--me-border)] bg-[color:var(--me-bg)] p-1.5"
                style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              >
                <div className="me-mono text-[10px]">
                  <span className="font-medium me-text">{tc.name}</span>
                  <span className="me-text-mute"> ({JSON.stringify(tc.args)})</span>
                </div>
                <div className="me-mono mt-0.5 text-[10px] me-text-mute">
                  → {tc.result_preview}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}


// Walk back from a final assistant row to the prior user message, and
// gather the tool messages that ran in this turn. Cross-reference each
// tool message's tool_call_id with the preceding mid-loop assistant's
// `tool_calls` list to recover args. Returns chip data in the same
// shape the renderer expects: { name, args, result_preview }.
function reconstructToolChips(
  messages: ChatPersistedMessage[],
  finalAssistantIdx: number,
): ChatToolCall[] {
  // Build a map tool_call_id → args from any mid-loop assistant in the
  // same turn (between the prior user and this assistant).
  const argsById = new Map<string, unknown>()
  const toolRows: ChatPersistedMessage[] = []
  for (let j = finalAssistantIdx - 1; j >= 0; j--) {
    const m = messages[j]
    if (m.role === 'user') break
    if (m.role === 'tool') {
      toolRows.unshift(m)
    } else if (m.role === 'assistant' && Array.isArray(m.tool_calls)) {
      for (const tc of m.tool_calls as Array<Record<string, unknown>>) {
        const id = (tc as { id?: string }).id
        const fn = (tc as { function?: { arguments?: unknown } }).function
        if (id != null && fn) {
          let parsed: unknown = fn.arguments
          if (typeof parsed === 'string') {
            try { parsed = JSON.parse(parsed) } catch { /* keep raw */ }
          }
          argsById.set(id, parsed ?? {})
        }
      }
    }
  }
  return toolRows.map((t) => {
    const raw = (t.tool_call_id && argsById.get(t.tool_call_id)) || {}
    const args: Record<string, unknown> = (raw && typeof raw === 'object' && !Array.isArray(raw))
      ? raw as Record<string, unknown>
      : {}
    return {
      name: t.tool_name || '',
      args,
      result_preview: (t.content || '').slice(0, 300),
    }
  })
}


// ─── Streaming markdown ─────────────────────────────────────────────────────
//
// Live-render markdown WITHOUT the per-chunk reflow.
//
// react-markdown re-parses every render, so as a stream completes
// partial markdown (`**bold` → `**bold**`, `# H` → `# Heading\n`) the
// DOM tree shifts and the user sees text "flash" or change size as
// nodes swap. Standard incremental-render pattern is to only re-parse
// at safe boundaries — newlines. The text up to the LAST newline is a
// stable prefix that only changes when a fresh line completes; we
// memoize the ReactMarkdown render of that prefix so chunks within
// the trailing line don't trigger a re-parse. The trailing partial
// line renders as plain text.
//
// Caveat: code-fence (```...```) lines stay rendered as the raw
// triple-backticks until the closing fence arrives — users almost
// never trip this in chat answers.

const _STREAM_MD_COMPONENTS = {
  p: (props: object) => <p className="mb-1.5 last:mb-0" {...props} />,
  ul: (props: object) => <ul className="my-1.5 list-disc pl-4 space-y-0.5" {...props} />,
  ol: (props: object) => <ol className="my-1.5 list-decimal pl-4 space-y-0.5" {...props} />,
  li: (props: object) => <li className="leading-snug" {...props} />,
  strong: (props: object) => <strong className="font-semibold" {...props} />,
  em: (props: object) => <em className="italic" {...props} />,
  code: (props: object) => (
    <code
      className="me-mono rounded bg-[color:var(--me-bg)] px-1 py-0.5 text-[11px]"
      {...props}
    />
  ),
  pre: (props: object) => (
    <pre
      className="me-mono my-2 max-h-[260px] overflow-auto rounded bg-[color:var(--me-bg)] p-2 text-[11px]"
      {...props}
    />
  ),
  h1: (props: object) => <div className="mb-1 mt-2 text-[13px] font-semibold" {...props} />,
  h2: (props: object) => <div className="mb-1 mt-2 text-[13px] font-semibold" {...props} />,
  h3: (props: object) => <div className="mb-1 mt-2 text-[12px] font-semibold" {...props} />,
  a: (props: object) => <a className="underline" target="_blank" rel="noreferrer" {...props} />,
}

function StreamingMarkdown({ text }: { text: string }) {
  // Split at last newline. Everything before is stable; trailing
  // partial line stays plain so it doesn't churn the markdown tree
  // on every keystroke.
  const lastNl = text.lastIndexOf('\n')
  const stable = lastNl >= 0 ? text.slice(0, lastNl) : ''
  const tail = lastNl >= 0 ? text.slice(lastNl + 1) : text

  // Memoize the stable-prefix ReactMarkdown render. Identity-stable
  // across renders that don't change `stable`, so the markdown tree
  // doesn't re-parse when only `tail` grows.
  const stableNode = useMemo(() => {
    if (!stable) return null
    return (
      <ReactMarkdown components={_STREAM_MD_COMPONENTS}>{stable}</ReactMarkdown>
    )
  }, [stable])

  return (
    <>
      {stableNode}
      {tail && (
        <span
          className="whitespace-pre-wrap"
          style={{ overflowWrap: 'anywhere' }}
        >
          {tail}
        </span>
      )}
    </>
  )
}


// ─── Copy button ────────────────────────────────────────────────────────────
//
// Small "copy assistant response" button. Copies the raw markdown of
// the message — NOT tool calls or tool results. Shows a check + label
// briefly after a successful copy.

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1400)
    } catch {
      // Older browsers / non-secure contexts: fallback via execCommand
      try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1400)
      } catch {
        // give up silently
      }
    }
  }
  return (
    <button
      onClick={onClick}
      className="me-text-mute hover:me-text inline-flex items-center px-0.5 py-0 select-none"
      style={{ userSelect: 'none' }}
      title={copied ? 'Copied' : 'Copy response'}
      aria-label={copied ? 'Copied' : 'Copy response'}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  )
}


// ─── Context meter ──────────────────────────────────────────────────────────
//
// Compact prompt-token usage indicator. Uses `used_reported` (real
// usage.prompt_tokens from the model) when available, falling back to
// our estimator. Tone shifts at 70% and 90% so the user gets early
// warning before compaction kicks in (default trim threshold is 80%).

function fmtK(n: number): string {
  if (n < 1000) return `${n}`
  return `${(n / 1000).toFixed(1)}K`
}

function ContextMeter({ ctx }: { ctx: ChatContextMeter }) {
  const used = ctx.used_reported || ctx.used_estimated || 0
  const budget = ctx.budget || 1
  const pct = Math.min(1, used / budget)
  const pctStr = `${Math.round(pct * 100)}%`
  const tone =
    pct >= 0.9 ? 'text-[color:var(--me-warn)]'
    : pct >= 0.7 ? 'text-[color:var(--me-stale,var(--me-warn))]'
    : 'me-text-mute'
  const title = (
    `${used} prompt tokens used of ${budget} budget (${pctStr}). ` +
    `Source: ${ctx.used_reported ? 'model usage report' : 'estimated'}. ` +
    `Trims when usage exceeds ~80%.`
  )
  return (
    <span
      className={`shrink-0 me-mono text-[10px] ${tone}`}
      title={title}
    >
      {fmtK(used)}/{fmtK(budget)} ({pctStr})
    </span>
  )
}


// ─── Bubble overlay ─────────────────────────────────────────────────────────
//
// Collapsed: a small Sparkles-icon button anchored bottom-LEFT (the
// Tweaks gear lives bottom-right, so we stay clear of it).
// Expanded: a panel anchored bottom-left, capped at min(70vh, 700px)
// so it doesn't dominate the screen on tall monitors. Width 380px.

type BubbleProps = {
  scopeLabel?: string | null
  scopeKind: ChatScopeKind
  scopeId: number | null
  /** Artifact selected in the UI right now (forwarded to ChatPanel). */
  currentArtifactId?: number | null
  /** Initial open/closed; persisted to localStorage so reload keeps state. */
  storageKey?: string
}

const BUBBLE_WIDTH = 380  // px

export function ChatBubble({
  scopeLabel, scopeKind, scopeId, currentArtifactId, storageKey = 'me_chat_open',
}: BubbleProps) {
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(storageKey) === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem(storageKey, open ? '1' : '0') } catch {}
  }, [storageKey, open])

  if (open) {
    return (
      <div
        className="fixed bottom-3 right-3 z-40 flex flex-col rounded-md border me-border bg-[color:var(--me-surface)] shadow-2xl"
        style={{
          width: BUBBLE_WIDTH,
          maxWidth: 'calc(100vw - 24px)',
          height: 'min(70vh, 700px)',
        }}
      >
        <ChatPanel
          scopeKind={scopeKind}
          scopeId={scopeId}
          scopeLabel={scopeLabel}
          currentArtifactId={currentArtifactId}
          onMinimize={() => setOpen(false)}
        />
      </div>
    )
  }

  return (
    <button
      onClick={() => setOpen(true)}
      className="fixed bottom-4 right-4 z-40 flex h-10 w-10 items-center justify-center rounded-full border me-border bg-[color:var(--me-surface)] shadow-lg hover:bg-[color:var(--me-bg)]"
      title={scopeLabel ? `Open chat — ${scopeLabel}` : 'Open chat'}
    >
      <Sparkles size={15} className="me-text" />
    </button>
  )
}
